import { useAuthContext } from '@offer/auth-lib'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  createFavoriteView,
  createFavoriteViewFolder,
  deleteFavoriteView,
  deleteFavoriteViewFolder,
  getFavoritesViews,
  getFavoriteViewFolders,
  updateFavoriteView,
  updateFavoriteViewFolder,
} from '@/api/favorites'
import { queryKeys } from '@/query/queryKeys'

const FAVORITES_STALE_TIME = 5 * 60 * 1000

export function useFavoriteViews(enabled = true, folderId?: string) {
  const { user } = useAuthContext()
  const userId = user?.id

  return useQuery({
    queryKey: queryKeys.favoriteViews(userId, folderId),
    queryFn: ({ signal }) => getFavoritesViews(folderId, signal),
    enabled: Boolean(userId) && enabled,
    staleTime: FAVORITES_STALE_TIME,
  })
}

export function useFavoriteViewFolders() {
  const { user } = useAuthContext()
  const userId = user?.id

  return useQuery({
    queryKey: queryKeys.favoriteViewFolders(userId),
    queryFn: ({ signal }) => getFavoriteViewFolders(signal),
    enabled: Boolean(userId),
    staleTime: FAVORITES_STALE_TIME,
  })
}

export function useCreateFavoriteViewFolder() {
  const { user } = useAuthContext()
  const queryClient = useQueryClient()
  const userId = user?.id

  return useMutation({
    mutationFn: createFavoriteViewFolder,
    onSuccess: (createdFolder) => {
      queryClient.setQueryData(
        queryKeys.favoriteViewFolders(userId),
        (previous: Awaited<ReturnType<typeof getFavoriteViewFolders>> | undefined) => previous
          ? { items: [...previous.items, createdFolder] }
          : { items: [createdFolder] },
      )
    },
  })
}

export function useCreateFavoriteView() {
  const { user } = useAuthContext()
  const queryClient = useQueryClient()
  const userId = user?.id

  return useMutation({
    mutationFn: createFavoriteView,
    onSuccess: (createdView) => {
      queryClient.setQueryData(
        queryKeys.favoriteViews(userId),
        (previous: Awaited<ReturnType<typeof getFavoritesViews>> | undefined) => previous
          ? { items: [...previous.items, createdView], total: previous.total + 1 }
          : { items: [createdView], total: 1 },
      )
      queryClient.setQueryData(
        queryKeys.favoriteViewFolders(userId),
        (previous: Awaited<ReturnType<typeof getFavoriteViewFolders>> | undefined) => previous
          ? {
              items: previous.items.map(folder => folder.id === createdView.folderId
                ? { ...folder, viewCount: folder.viewCount + 1 }
                : folder),
            }
          : previous,
      )
      void queryClient.invalidateQueries({ queryKey: queryKeys.favoriteViews(userId) })
    },
  })
}

export function useUpdateFavoriteView() {
  const { user } = useAuthContext()
  const queryClient = useQueryClient()
  const userId = user?.id

  return useMutation({
    mutationFn: ({ viewId, input }: { viewId: string, input: Parameters<typeof updateFavoriteView>[1] }) => updateFavoriteView(viewId, input),
    onSuccess: (updatedView, variables) => {
      let previousFolderId: string | null = null

      queryClient.setQueryData(
        queryKeys.favoriteViews(userId),
        (previous: Awaited<ReturnType<typeof getFavoritesViews>> | undefined) => {
          if (!previous)
            return previous

          previousFolderId = previous.items.find(view => view.id === variables.viewId)?.folderId ?? null
          return {
            ...previous,
            items: previous.items.map(view => view.id === updatedView.id ? updatedView : view),
          }
        },
      )
      if (previousFolderId !== updatedView.folderId) {
        queryClient.setQueryData(
          queryKeys.favoriteViewFolders(userId),
          (previous: Awaited<ReturnType<typeof getFavoriteViewFolders>> | undefined) => previous
            ? {
                items: previous.items.map(folder => ({
                  ...folder,
                  viewCount: Math.max(0, folder.viewCount
                  + (folder.id === updatedView.folderId ? 1 : 0)
                  - (folder.id === previousFolderId ? 1 : 0)),
                })),
              }
            : previous,
        )
      }
      void queryClient.invalidateQueries({ queryKey: queryKeys.favoriteViews(userId) })
    },
  })
}

export function useDeleteFavoriteView() {
  const { user } = useAuthContext()
  const queryClient = useQueryClient()
  const userId = user?.id

  return useMutation({
    mutationFn: deleteFavoriteView,
    onSuccess: (_result, viewId) => {
      let deletedFolderId: string | null = null

      queryClient.setQueryData(
        queryKeys.favoriteViews(userId),
        (previous: Awaited<ReturnType<typeof getFavoritesViews>> | undefined) => {
          if (!previous)
            return previous

          const deletedView = previous.items.find(view => view.id === viewId)
          deletedFolderId = deletedView?.folderId ?? null

          return {
            items: previous.items.filter(view => view.id !== viewId),
            total: Math.max(0, previous.total - (deletedView ? 1 : 0)),
          }
        },
      )
      if (deletedFolderId) {
        queryClient.setQueryData(
          queryKeys.favoriteViewFolders(userId),
          (previous: Awaited<ReturnType<typeof getFavoriteViewFolders>> | undefined) => previous
            ? {
                items: previous.items.map(folder => folder.id === deletedFolderId
                  ? { ...folder, viewCount: Math.max(0, folder.viewCount - 1) }
                  : folder),
              }
            : previous,
        )
      }
      void queryClient.invalidateQueries({ queryKey: queryKeys.favoriteViews(userId) })
    },
  })
}

export function useUpdateFavoriteViewFolder() {
  const { user } = useAuthContext()
  const queryClient = useQueryClient()
  const userId = user?.id

  return useMutation({
    mutationFn: ({ folderId, input }: { folderId: string, input: Parameters<typeof updateFavoriteViewFolder>[1] }) => updateFavoriteViewFolder(folderId, input),
    onSuccess: (updatedFolder) => {
      queryClient.setQueryData(
        queryKeys.favoriteViewFolders(userId),
        (previous: Awaited<ReturnType<typeof getFavoriteViewFolders>> | undefined) => previous
          ? { items: previous.items.map(folder => folder.id === updatedFolder.id ? updatedFolder : folder) }
          : previous,
      )
    },
  })
}

export function useDeleteFavoriteViewFolder() {
  const { user } = useAuthContext()
  const queryClient = useQueryClient()
  const userId = user?.id

  return useMutation({
    mutationFn: deleteFavoriteViewFolder,
    onSuccess: (_result, folderId) => {
      queryClient.setQueryData(
        queryKeys.favoriteViewFolders(userId),
        (previous: Awaited<ReturnType<typeof getFavoriteViewFolders>> | undefined) => previous
          ? { items: previous.items.filter(folder => folder.id !== folderId) }
          : previous,
      )
      queryClient.setQueriesData(
        { queryKey: queryKeys.favoriteViews(userId) },
        (previous: Awaited<ReturnType<typeof getFavoritesViews>> | undefined) => previous
          ? {
              ...previous,
              items: previous.items.filter(view => view.folderId !== folderId),
              total: Math.max(0, previous.total - previous.items.filter(view => view.folderId === folderId).length),
            }
          : previous,
      )
      void queryClient.invalidateQueries({ queryKey: queryKeys.favoriteViews(userId) })
    },
  })
}
